import React, { useState, useEffect } from 'react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import PendingApprovalCard from './components/PendingApprovalCard';
import AnalyticsCard from './components/AnalyticsCard';
import UserDetailsModal from './components/UserDetailsModal';
import ActiveUsersTable from './components/ActiveUsersTable';
import UsageChart from './components/UsageChart';
import UsageTrendsChart from './components/UsageTrendsChart';

import AddInstitutionModal from './components/AddInstitutionModal';
import UserAnalyticsSection from './components/UserAnalyticsSection';
import AddCreditsModal from './components/AddCreditsModal';
import RejectionReasonModal from './components/RejectionReasonModal';
import RevenueAnalyticsSection from './components/RevenueAnalyticsSection';
import { useAuth } from '../../contexts/AuthContext';
import { getPendingDocumentApprovals, updateUserVerificationStatus } from '../../services/documentService';
import adminService from '../../services/adminService';
import { sendVerificationEmail } from '../../services/documentService';
import { RefreshCw, Users, Building, BarChart3, CheckSquare, Search, Filter, Coins, DollarSign } from 'lucide-react';
import Icon from '../../components/AppIcon';


export default function AdminDashboard() {
  const [selectedTab, setSelectedTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [filterSpecialty, setFilterSpecialty] = useState('all');
  const [filterInstitution, setFilterInstitution] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [selectedApprovals, setSelectedApprovals] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const { user } = useAuth();
  const profile = user;
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [actionError, setActionError] = useState(null);
  const [showAddInstitutionModal, setShowAddInstitutionModal] = useState(false);
  const [showAddCreditsModal, setShowAddCreditsModal] = useState(false);
  const [selectedUserForCredits, setSelectedUserForCredits] = useState(null);
  const [stats, setStats] = useState({ 
    totalUsers: 0,
    pendingApprovals: 0,
    activeToday: 0,
    totalRecommendations: 0 
  });
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [userToReject, setUserToReject] = useState(null);

  const specialtyOptions = [
    { value: 'all', label: 'All Specialties' },
    { value: 'cardiology', label: 'Cardiology' },
    { value: 'neurology', label: 'Neurology' },
    { value: 'radiology', label: 'Radiology' },
    { value: 'orthopedics', label: 'Orthopedics' }
  ];

  const institutionOptions = [
    { value: 'all', label: 'All Institutions' }
  ];

  useEffect(() => {
    loadDashboardData();
    
    // Set up auto-refresh every 30 seconds for real-time updates
    const refreshInterval = setInterval(() => {
      fetchStats();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(refreshInterval);
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadPendingApprovals(),
        fetchStats()
      ]);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setActionError(error?.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      // Fetch real-time statistics from database using RPC function
      const statsData = await adminService?.getDashboardStats();
      
      setStats({
        totalUsers: statsData?.totalUsers || 0,
        pendingApprovals: statsData?.pendingApprovals || 0,
        activeToday: statsData?.activeToday || 0,
        totalRecommendations: statsData?.totalRecommendations || 0
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
      setActionError('Failed to fetch dashboard statistics. Please try refreshing.');
      // Keep existing stats on error to prevent UI from breaking
    }
  };

  const loadPendingApprovals = async () => {
    try {
      const approvals = await getPendingDocumentApprovals();
      
      const formattedApprovals = approvals?.map(approval => ({
        id: approval?.id,
        name: approval?.full_name,
        email: approval?.email,
        phoneNumber: approval?.phone_number,
        licenseNumber: approval?.license_number,
        licensedState: approval?.institution_country,
        specialty: approval?.specialization,
        institution: approval?.institution_name,
        institutionType: approval?.institution_type || 'Hospital',
        location: approval?.institution_country,
        yearsOfPractice: approval?.years_of_practice,
        licenseVerified: approval?.documents?.some(d => 
          d?.document_type === 'medical_license' && d?.verified_at
        ),
        institutionVerified: approval?.documents?.some(d => 
          d?.document_type === 'clinical_affiliation' && d?.verified_at
        ),
        registrationDate: approval?.created_at,
        documents: approval?.documents || []
      })) || [];

      setPendingApprovals(formattedApprovals);
    } catch (error) {
      console.error('Error loading pending approvals:', error);
      setActionError(error?.message);
    }
  };

  const handleApprove = async (userId) => {
    try {
      setApproving(true);
      setActionError(null);
      
      // Find user details
      const user = pendingApprovals?.find(u => u?.id === userId);
      if (!user) {
        throw new Error('User not found');
      }

      // Update verification status
      await updateUserVerificationStatus(userId, 'verified', 'Application approved by admin');
      
      // Send approval email
      try {
        await sendVerificationEmail(user?.email, user?.name, 'approved');
      } catch (emailError) {
        console.error('Failed to send approval email:', emailError);
        // Continue even if email fails
      }
      
      await loadDashboardData();
      
      alert('User approved successfully! Confirmation email has been sent.');
    } catch (error) {
      console.error('Error approving user:', error);
      setActionError(error?.message);
      alert('Error: ' + error?.message);
    } finally {
      setApproving(false);
    }
  };

  const handleReject = (userId) => {
    const user = pendingApprovals?.find(u => u?.id === userId);
    if (user) {
      setUserToReject(user);
      setShowRejectionModal(true);
    }
  };

  const handleRejectConfirm = async (reason) => {
    if (!userToReject) return;

    try {
      setApproving(true);
      setActionError(null);
      
      // Update verification status
      const reasonText = reason === 'insufficient_documentation' 
        ? 'Insufficient documentation provided'
        : reason === 'poor_image_quality' ?'Poor image quality of documents' :'Application requires additional review';

      await updateUserVerificationStatus(userToReject?.id, 'rejected', reasonText);
      
      // Send rejection email
      try {
        await sendVerificationEmail(
          userToReject?.email, 
          userToReject?.name, 
          'rejected',
          reason
        );
      } catch (emailError) {
        console.error('Failed to send rejection email:', emailError);
        // Continue even if email fails
      }
      
      await loadDashboardData();
      setShowRejectionModal(false);
      setUserToReject(null);
      
      alert('User application rejected. Notification email has been sent.');
    } catch (error) {
      console.error('Error rejecting user:', error);
      setActionError(error?.message);
      alert('Error: ' + error?.message);
    } finally {
      setApproving(false);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedApprovals?.length === 0) {
      alert('Please select users to approve');
      return;
    }

    try {
      setApproving(true);
      setActionError(null);
      
      await Promise.all(
        selectedApprovals?.map(userId => 
          updateUserVerificationStatus(userId, 'verified', 'Bulk approved by admin')
        )
      );
      
      setSelectedApprovals([]);
      await loadDashboardData();
      
      alert('Selected users approved successfully!');
    } catch (error) {
      console.error('Error bulk approving:', error);
      setActionError(error?.message);
    } finally {
      setApproving(false);
    }
  };

  const handleSelectApproval = (userId) => {
    setSelectedApprovals((prev) =>
      prev?.includes(userId) ?
        prev?.filter((id) => id !== userId) :
        [...prev, userId]
    );
  };

  const handleViewDetails = (user) => {
    setSelectedUser(user);
    setShowUserDetails(true);
  };

  const handleAddInstitution = () => {
    setShowAddInstitutionModal(true);
  };

  const handleCloseInstitutionModal = () => {
    setShowAddInstitutionModal(false);
  };

  const handleSaveInstitution = (institutionData) => {
    console.log('Saving institution:', institutionData);
    alert('Institution saved successfully!');
    setShowAddInstitutionModal(false);
  };

  const handleAddCredits = () => {
    setShowAddCreditsModal(true);
  };

  const handleAddCreditsToUser = (user) => {
    setSelectedUserForCredits(user);
    setShowAddCreditsModal(true);
  };

  const handleCloseCreditsModal = () => {
    setShowAddCreditsModal(false);
    setSelectedUserForCredits(null);
  };

  const handleCreditsSuccess = (result) => {
    alert(result?.message);
    setShowAddCreditsModal(false);
    loadDashboardData(); // Refresh data
  };

  const filteredApprovals = pendingApprovals?.filter((user) => {
    const matchesSearch = user?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      user?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesSpecialty = filterSpecialty === 'all' ||
      user?.specialty?.toLowerCase()?.includes(filterSpecialty?.toLowerCase());
    const matchesInstitution = filterInstitution === 'all' ||
      user?.institution?.toLowerCase()?.includes(filterInstitution?.toLowerCase());
    return matchesSearch && matchesSpecialty && matchesInstitution;
  });

  const analyticsData = [
    {
      title: 'Total Users',
      value: stats?.totalUsers?.toString() || '0',
      change: '+12%',
      trend: 'up',
      icon: 'Users'
    },
    {
      title: 'Pending Approvals',
      value: stats?.pendingApprovals?.toString() || '0',
      change: '-5%',
      trend: 'down',
      icon: 'Clock'
    },
    {
      title: 'Active Today',
      value: stats?.activeToday?.toString() || '0',
      change: '+8%',
      trend: 'up',
      icon: 'Activity'
    },
    {
      title: 'Total Recommendations',
      value: stats?.totalRecommendations?.toString() || '0',
      change: '+15%',
      trend: 'up',
      icon: 'FileText'
    }
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'approvals', label: 'Pending Approvals', icon: CheckSquare },
    { id: 'users', label: 'User Management', icon: Users },
    { id: 'revenue', label: 'Revenue Analytics', icon: DollarSign },
    { id: 'institutions', label: 'Institutions', icon: Building }
  ];

  return (
    <div className="min-h-screen bg-background">
      <PrimaryNavigation userRole={profile?.role || 'admin'} />

      <div className="pt-header">
        <div className="max-w-7xl mx-auto container-padding py-6 sm:py-8 md:py-12">
          {/* Header with Action Buttons - FIXED SPACING */}
          <div className="mb-6">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="flex-1">
                  <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
                  <p className="mt-1 text-sm text-gray-600">Manage users, institutions, and platform analytics</p>
                </div>
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={loadDashboardData}
                    disabled={loading}
                    className="flex-shrink-0"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleAddCredits}
                    className="flex-shrink-0"
                  >
                    <Coins className="w-4 h-4 mr-2" />
                    Add Credits
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleAddInstitution}
                    className="flex-shrink-0"
                  >
                    <Building className="w-4 h-4 mr-2" />
                    Add Institution
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {actionError && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Error</h3>
                  <p className="mt-1 text-sm text-red-700">{actionError}</p>
                </div>
                <button
                  onClick={() => setActionError(null)}
                  className="ml-auto text-red-400 hover:text-red-600"
                >
                  ×
                </button>
              </div>
            </div>
          )}

          {/* Tab Navigation */}
          <div className="mb-8 border-b border-gray-200">
            <nav className="flex space-x-8 overflow-x-auto">
              {tabs?.map((tab) => {
                const Icon = tab?.icon;
                return (
                  <button
                    key={tab?.id}
                    onClick={() => setSelectedTab(tab?.id)}
                    className={`
                      flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap
                      ${selectedTab === tab?.id
                        ? 'border-blue-500 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }
                    `}
                  >
                    <Icon className="w-5 h-5" />
                    {tab?.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Overview Tab */}
          {selectedTab === 'overview' && (
            <div className="space-y-8">
              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {analyticsData?.map((data, index) => (
                  <AnalyticsCard key={index} {...data} />
                ))}
              </div>

              {/* Usage Trends Analytics - NEW COMPREHENSIVE SECTION */}
              <UsageTrendsChart />

              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <UsageChart 
                  data={[]} 
                  title="Usage Overview" 
                  totalRecommendations={stats?.totalRecommendations} 
                />
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                  <div className="space-y-3">
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => setSelectedTab('approvals')}
                    >
                      <CheckSquare className="w-5 h-5 mr-3" />
                      Review Pending Approvals ({stats?.pendingApprovals})
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => setSelectedTab('users')}
                    >
                      <Users className="w-5 h-5 mr-3" />
                      Manage Users
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => setSelectedTab('revenue')}
                    >
                      <DollarSign className="w-5 h-5 mr-3" />
                      View Revenue Analytics
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={handleAddInstitution}
                    >
                      <Building className="w-5 h-5 mr-3" />
                      Add New Institution
                    </Button>
                  </div>
                </div>
              </div>

              {/* User Analytics */}
              <UserAnalyticsSection />
            </div>
          )}

          {/* Pending Approvals Tab */}
          {selectedTab === 'approvals' && (
            <div className="space-y-6">
              {/* Search and Filter Bar */}
              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <Input
                      placeholder="Search by name or email..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e?.target?.value)}
                      icon={<Search className="w-5 h-5 text-gray-400" />}
                    />
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <Filter className="w-4 h-4 mr-2" />
                    Filters
                  </Button>
                </div>

                {showFilters && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 pt-4 border-t">
                    <Select
                      options={specialtyOptions}
                      value={filterSpecialty}
                      onChange={(e) => setFilterSpecialty(e?.target?.value)}
                      label="Filter by Specialty"
                    />
                    <Select
                      options={institutionOptions}
                      value={filterInstitution}
                      onChange={(e) => setFilterInstitution(e?.target?.value)}
                      label="Filter by Institution"
                    />
                  </div>
                )}
              </div>

              {/* Bulk Actions */}
              {selectedApprovals?.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-blue-900">
                      {selectedApprovals?.length} user(s) selected
                    </span>
                    <div className="flex gap-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedApprovals([])}
                      >
                        Clear Selection
                      </Button>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={handleBulkApprove}
                        disabled={approving}
                      >
                        {approving ? 'Processing...' : 'Approve Selected'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Approvals Grid */}
              {loading ? (
                <div className="text-center py-12">
                  <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-4" />
                  <p className="text-gray-500">Loading pending approvals...</p>
                </div>
              ) : filteredApprovals?.length === 0 ? (
                <div className="bg-white rounded-lg shadow p-12 text-center">
                  <CheckSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">All caught up!</h3>
                  <p className="text-gray-500">No pending approvals at the moment.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {filteredApprovals?.map((approval) => (
                    <div key={approval?.id} className="relative">
                      <input
                        type="checkbox"
                        checked={selectedApprovals?.includes(approval?.id)}
                        onChange={() => handleSelectApproval(approval?.id)}
                        className="absolute top-4 left-4 w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 z-10"
                      />
                      <div className="pl-8">
                        <PendingApprovalCard
                          user={approval}
                          onApprove={handleApprove}
                          onReject={handleReject}
                          onViewDetails={handleViewDetails}
                          onRefresh={loadDashboardData}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* User Management Tab */}
          {selectedTab === 'users' && (
            <div className="space-y-6">
              {/* Search Bar for User Management */}
              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <Input
                      placeholder="Search users by name, email, specialty, or institution..."
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e?.target?.value)}
                      icon={<Search className="w-5 h-5 text-gray-400" />}
                    />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow">
                <div className="p-6 border-b border-gray-200">
                  <h2 className="text-xl font-semibold text-gray-900">Active Users</h2>
                  <p className="mt-1 text-sm text-gray-500">Manage and monitor all registered users</p>
                </div>
                <ActiveUsersTable 
                  searchQuery={userSearchQuery}
                  onSuspend={(userId) => console.log('Suspend user:', userId)}
                  onViewActivity={(userId) => console.log('View activity:', userId)}
                  onUserClick={handleViewDetails}
                  onAddCredits={handleAddCreditsToUser}
                />
              </div>
            </div>
          )}

          {/* Revenue Analytics Tab */}
          {selectedTab === 'revenue' && (
            <div className="space-y-6">
              <RevenueAnalyticsSection />
            </div>
          )}

          {/* Institutions Tab */}
          {selectedTab === 'institutions' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Institutional Accounts</h2>
                  <p className="mt-1 text-sm text-gray-500">Manage partner institutions and their accounts</p>
                </div>
                <Button
                  variant="default"
                  onClick={handleAddInstitution}
                >
                  <Building className="w-4 h-4 mr-2" />
                  Add Institution
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Placeholder for institutions */}
                <div className="col-span-full bg-white rounded-lg shadow p-12 text-center">
                  <Building className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No institutions yet</h3>
                  <p className="text-gray-500 mb-4">Start by adding your first institutional partner</p>
                  <Button variant="default" onClick={handleAddInstitution}>
                    Add Institution
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Modals */}
      {showUserDetails && selectedUser && (
        <UserDetailsModal
          user={selectedUser}
          onClose={() => setShowUserDetails(false)}
          onApprove={handleApprove}
          onReject={handleReject}
        />
      )}
      {showAddInstitutionModal && (
        <AddInstitutionModal
          onClose={handleCloseInstitutionModal}
          onSave={handleSaveInstitution}
        />
      )}
      {showAddCreditsModal && (
        <AddCreditsModal
          onClose={handleCloseCreditsModal}
          onSuccess={handleCreditsSuccess}
          preselectedUser={selectedUserForCredits}
        />
      )}
      {showRejectionModal && userToReject && (
        <RejectionReasonModal
          user={userToReject}
          onClose={() => {
            setShowRejectionModal(false);
            setUserToReject(null);
          }}
          onConfirm={handleRejectConfirm}
          isSubmitting={approving}
        />
      )}
    </div>
  );
}